/**
 * sourcePick — 검수 모달을 열 때 **이 문제의 자리**를 가리키는 소스 조각을 고른다.
 *
 * ── 왜 필요했나 (2026-09-06 오너 보고) ──
 * 「인덱스를 열었는데 미리보기의 빨간 상자가 다른 문제 자리를 가리킨다.」
 * 검수 상세의 `sources` 는 **같은 번호(match_key)의 조각 전부 ∪ 이 행이 직접 참조하는 조각**이다
 * (회신 `BE-20260906-reply-source-frag-shared.md` §2 Q1 — 의도된 동작이다. 문제지·해설에 같은 문제가
 * 두 번 실리거나 분할된 자식이 있으면 후보가 여럿이고, 검수자가 대조해 고를 수 있게 남겨 둔다).
 * 그래서 목록에 남의 조각이 섞여 있고, 화면이 첫째를 골라 두면 그 남의 자리에 상자를 그렸다.
 *
 * ── 이제 서버가 알려 준다 (같은 회신 §1 · dev `537792e`) ──
 *   · `sources[].is_body` — 이 행이 **직접 참조하는** 조각이면 true(kind 1 본문 · 2 풀이 · 3 정답 각각).
 *   · 상세 최상위 `body_frag_uuid` · `answer_frag_uuid` · `solution_frag_uuids[]` — 그 조각의 uuid.
 *   · 정렬 계약 — `kind` 순, 같은 kind 안에서는 직접 참조 조각이 언제나 먼저, 그 다음 `page_index`.
 * 정렬만 믿어도 첫째가 옳지만, 회신이 **`is_body` 로 고르기를 권했다** — 순서는 눈에 안 보이는 약속이고
 * 표시는 값이라, 서버가 정렬을 바꿔도 이 판정은 그대로 선다(2026-09-06 에 순서를 믿다가 겪은 일이다).
 *
 * `is_body` 가 없는 배포(구 서버)에서는 전부 `undefined` 라 첫째로 물러난다 — 종전 동작 그대로다.
 */

/** 그 조각이 이 문제가 직접 참조하는 것인가 — 서버가 참이라 말한 것만 참이다(키가 없으면 모른다). */
export const isDirectSource = (s) => s?.is_body === true;

/** 이 문제의 자리를 서버가 알려 줬는가 — 하나라도 표시가 있으면 참. 화면 문구가 이 값으로 갈린다. */
export function hasDirectSource(sources) {
  return (Array.isArray(sources) ? sources : []).some(isDirectSource);
}

/**
 * 창을 열 때 골라 둘 조각의 **인덱스**.
 *
 * 차례: ① 「문제」(kind 1)이면서 직접 참조인 것 → ② 종류를 가리지 않고 직접 참조인 것 →
 * ③ 「문제」인 것 → ④ 첫째. 창을 열면 문제부터 보는 것이 맞고(정답·풀이는 탭으로 옮긴다),
 * 표시가 없는 서버에서는 종전과 같은 자리에 선다.
 */
export function pickBodySourceIndex(sources) {
  const list = Array.isArray(sources) ? sources : [];
  if (list.length < 2) return 0;
  const at = (pred) => list.findIndex(pred);
  const bodyDirect = at((s) => Number(s?.kind) === 1 && isDirectSource(s));
  if (bodyDirect >= 0) return bodyDirect;
  const anyDirect = at(isDirectSource);
  if (anyDirect >= 0) return anyDirect;
  const anyBody = at((s) => Number(s?.kind) === 1);
  return anyBody >= 0 ? anyBody : 0;
}

export default pickBodySourceIndex;
